import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { StartupAccount, StartupOpportunity } from "@/types/startup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Briefcase, Calendar, DollarSign, ExternalLink } from "lucide-react";

interface StartupOpportunitiesProps {
  startupAccount: StartupAccount;
}

const OPPORTUNITY_TYPES = [
  "internship", "task", "micro-project", "collaboration"
];

const StartupOpportunities: React.FC<StartupOpportunitiesProps> = ({ startupAccount }) => {
  const { toast } = useToast();
  const [opportunities, setOpportunities] = useState<StartupOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    opportunity_type: "",
    requirements: "",
    compensation: "",
    duration: "",
    deadline: "",
    application_url: ""
  });

  useEffect(() => {
    fetchOpportunities();
  }, [startupAccount.id]);

  const fetchOpportunities = async () => {
    try {
      const { data, error } = await supabase
        .from("startup_opportunities")
        .select("*")
        .eq("startup_account_id", startupAccount.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setOpportunities(data || []);
    } catch (error) {
      console.error("Error fetching opportunities:", error);
      toast({
        title: "Error",
        description: "Failed to load opportunities",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim() || !formData.opportunity_type) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      setSubmitting(true);

      const requirements = formData.requirements
        .split('\n')
        .map(req => req.trim())
        .filter(req => req.length > 0);

      const { error } = await supabase
        .from("startup_opportunities")
        .insert({
          startup_account_id: startupAccount.id,
          title: formData.title.trim(),
          description: formData.description.trim(),
          opportunity_type: formData.opportunity_type as any,
          requirements: requirements.length > 0 ? requirements : null,
          compensation: formData.compensation.trim() || null,
          duration: formData.duration.trim() || null,
          deadline: formData.deadline || null,
          application_url: formData.application_url.trim() || null
        });

      if (error) throw error;

      toast({
        title: "Opportunity Created",
        description: "Your opportunity has been posted successfully"
      });

      setFormData({
        title: "", description: "", opportunity_type: "", requirements: "",
        compensation: "", duration: "", deadline: "", application_url: ""
      });
      setDialogOpen(false);
      fetchOpportunities();
    } catch (error) {
      console.error("Error creating opportunity:", error);
      toast({
        title: "Error",
        description: "Failed to create opportunity",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8C00]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Opportunities</h2>
          <p className="text-muted-foreground">Post internships and projects for teens</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Post Opportunity
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Post New Opportunity</DialogTitle>
              <DialogDescription>
                Create an opportunity for teen users
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Summer Internship, UI Design Task"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="opportunity_type">Type *</Label>
                <Select value={formData.opportunity_type} onValueChange={(value) => setFormData(prev => ({ ...prev, opportunity_type: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {OPPORTUNITY_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the opportunity, responsibilities, and what teens will learn..."
                  rows={4}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="requirements">Requirements (one per line)</Label>
                <Textarea
                  id="requirements"
                  value={formData.requirements}
                  onChange={(e) => setFormData(prev => ({ ...prev, requirements: e.target.value }))}
                  placeholder="High school student&#10;Basic design skills&#10;Available 10 hours/week"
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="compensation">Compensation</Label>
                  <Input
                    id="compensation"
                    value={formData.compensation}
                    onChange={(e) => setFormData(prev => ({ ...prev, compensation: e.target.value }))}
                    placeholder="$15/hour, Unpaid, etc."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration</Label>
                  <Input
                    id="duration"
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                    placeholder="3 months, 1 week, etc."
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="deadline">Application Deadline</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="application_url">Application URL</Label>
                <Input
                  id="application_url"
                  type="url"
                  value={formData.application_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, application_url: e.target.value }))}
                  placeholder="https://forms.google.com/..."
                />
              </div>
              
              <div className="flex gap-2">
                <Button type="submit" disabled={submitting} className="flex-1">
                  {submitting ? "Posting..." : "Post Opportunity"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {opportunities.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-card rounded-xl shadow-2xl border-0 p-8 backdrop-blur-sm">
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-[#FF8C00] rounded-full flex items-center justify-center mb-6">
                <Briefcase className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-3 text-[#FF8C00]">
                No Opportunities Posted Yet
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Start connecting with talented teens by posting your first opportunity. Share internships, projects, or collaborations.
              </p>
              <div className="space-y-3">
                <Button 
                  className="w-full bg-[#FF8C00] hover:bg-[#FF7F00] text-white border-0 h-12 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl" 
                  onClick={() => setDialogOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Post Your First Opportunity
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid gap-6">
          {opportunities.map((opportunity) => (
            <Card key={opportunity.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {opportunity.title}
                      <Badge variant="outline">
                        {opportunity.opportunity_type.charAt(0).toUpperCase() + 
                         opportunity.opportunity_type.slice(1).replace('-', ' ')}
                      </Badge>
                      {!opportunity.is_active && (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-2">
                      {opportunity.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {opportunity.requirements && opportunity.requirements.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-1">Requirements:</h4>
                      <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                        {opportunity.requirements.map((req, index) => (
                          <li key={index}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                    {opportunity.compensation && (
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        {opportunity.compensation}
                      </div>
                    )}
                    {opportunity.duration && (
                      <div className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        {opportunity.duration}
                      </div>
                    )}
                    {opportunity.deadline && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Deadline: {new Date(opportunity.deadline).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    {opportunity.application_url && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={opportunity.application_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Apply
                        </a>
                      </Button>
                    )}
                    <Button variant="outline" size="sm">
                      Edit
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default StartupOpportunities;
